import { Box, CircularProgress } from '@mui/material';
import { useFormik } from 'formik';
import { useRef } from 'react';
import toast from 'react-hot-toast';
import AppModal from './AppModal';
import FilledButton from './FilledButton';
import PasswordInputBox from './PasswordInputBox';
import PasswordPolicyChecklist from './PasswordPolicyChecklist';
import useAPI from '../../services/api.service';
import { changePasswordSchema } from '../../validators/auth.validators';

/**
 * Change Password, opened from the account menu (TASK-2026-10-12-002).
 *
 *   POST /api/v1/users/change-password { oldPassword, newPassword, confirmPassword }
 *
 * The user is whoever holds the access token — the body carries no user id.
 * The policy and the match are checked here AND by the backend; whether the
 * old password is right only the backend knows, and it says so with a 400,
 * which lands inline on the Old Password field.
 *
 * While the request is in flight both buttons are disabled and the dialog
 * cannot be closed, and a ref guards against a second submit (Enter pressed
 * again before React re-renders the disabled button). The form is cleared
 * whenever the dialog closes, so typed passwords never linger in state.
 */

const FORM_ID = 'change-password-form';
const FIELDS = ['oldPassword', 'newPassword', 'confirmPassword'];
const INITIAL_VALUES = { oldPassword: '', newPassword: '', confirmPassword: '' };

/**
 * Put a failed request's message where the user will look for it.
 * @returns {void}
 */
const showServerError = (error, setFieldError) => {
  const status = error?.response?.status;
  const payload = error?.response?.data;

  if (status === 400) {
    setFieldError('oldPassword', payload?.message || 'Old password is incorrect.');
    return;
  }

  // Validator failures: { errors: [{ newPassword: 'msg' }, ...] }.
  if (status === 422 && Array.isArray(payload?.errors)) {
    const known = payload.errors
      .flatMap((entry) => Object.entries(entry ?? {}))
      .filter(([field]) => FIELDS.includes(field));
    known.forEach(([field, message]) => setFieldError(field, message));
    if (known.length) return;
  }

  toast.error(payload?.message || 'Could not change your password. Please try again.', {
    id: 'change-password-error',
  });
};

const ChangePasswordModal = ({ open, onClose }) => {
  const API = useAPI();
  const inFlight = useRef(false);

  const formik = useFormik({
    initialValues: INITIAL_VALUES,
    validationSchema: changePasswordSchema,
    onSubmit: async (values, { resetForm, setFieldError }) => {
      if (inFlight.current) return;
      inFlight.current = true;
      try {
        await API.changePassword(values);
        toast.success('Your password has been changed.', { id: 'change-password-success' });
        resetForm();
        onClose();
      } catch (error) {
        showServerError(error, setFieldError);
      } finally {
        inFlight.current = false;
      }
    },
  });

  const submitting = formik.isSubmitting;

  const handleClose = () => {
    if (submitting || inFlight.current) return;
    formik.resetForm();
    onClose();
  };

  return (
    <AppModal
      open={open}
      onClose={handleClose}
      title="Change Password"
      subtitle="Enter your current password, then choose a new one."
      maxWidth="xs"
      actions={
        <>
          <FilledButton
            btnname="Cancel"
            color="#9AA3AF"
            onClick={handleClose}
            disabled={submitting}
          />
          <FilledButton
            btnname={submitting ? 'Updating…' : 'Update Password'}
            type="submit"
            form={FORM_ID}
            disabled={submitting}
            Icon={submitting ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
          />
        </>
      }
    >
      <Box
        component="form"
        id={FORM_ID}
        noValidate
        onSubmit={formik.handleSubmit}
        sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
      >
        <PasswordInputBox
          label="Old Password"
          name="oldPassword"
          formik={formik}
          required
          autoComplete="current-password"
        />
        <Box>
          <PasswordInputBox
            label="New Password"
            name="newPassword"
            formik={formik}
            required
            autoComplete="new-password"
          />
          <PasswordPolicyChecklist password={formik.values.newPassword} />
        </Box>
        <PasswordInputBox
          label="Confirm New Password"
          name="confirmPassword"
          formik={formik}
          required
          autoComplete="new-password"
        />
      </Box>
    </AppModal>
  );
};

export default ChangePasswordModal;
